import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  DollarSign,
  Plus,
  CheckCircle2,
  Clock,
  XCircle,
  Laptop,
  MoreHorizontal,
  Send,
  Repeat,
  Pencil,
} from "lucide-react";

// Only two categories as requested
const EXPENSE_CATEGORIES = {
  software: { label: "Software", icon: Laptop, color: "text-purple-400" },
  other: { label: "Other", icon: MoreHorizontal, color: "text-zinc-400" },
};

const MyExpensesPage = () => {
  const { user } = useAuth();
  const { formatAmount } = useCurrency();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  // id of the pending expense being edited (null = creating a new one)
  const [editingId, setEditingId] = useState(null);

  // New expense form
  const [newExpense, setNewExpense] = useState({
    amount: "",
    currency: "UAH",
    expense_type: "software",
    description: "",
    date: new Date().toISOString().slice(0, 10),
    // one_time (default) | monthly — monthly auto-recreates each month
    recurrence: "one_time",
  });

  useEffect(() => {
    fetchExpenses();
  }, []);

  const fetchExpenses = async () => {
    try {
      const res = await api.get("/my-expenses");
      setExpenses(res.data || []);
    } catch (error) {
      console.error("Error fetching expenses:", error);
      // Don't show error for 404 - just means no expenses yet
      if (error.response?.status !== 404) {
        toast.error("Failed to load expenses");
      }
    } finally {
      setLoading(false);
    }
  };

  const emptyForm = {
    amount: "",
    currency: "UAH",
    expense_type: "software",
    description: "",
    date: new Date().toISOString().slice(0, 10),
    recurrence: "one_time",
  };

  const openNew = () => {
    setEditingId(null);
    setNewExpense(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (expense) => {
    setEditingId(expense.id);
    setNewExpense({
      amount: expense.amount ?? "",
      currency: expense.currency || "UAH",
      expense_type: expense.expense_type || "software",
      description: expense.description || expense.notes || "",
      date: (expense.date || new Date().toISOString().slice(0, 10)).slice(0, 10),
      recurrence: expense.recurrence || "one_time",
    });
    setDialogOpen(true);
  };

  const handleSubmitExpense = async () => {
    if (!newExpense.amount || !newExpense.description) {
      toast.error("Please fill in amount and description");
      return;
    }

    setSubmitting(true);
    try {
      if (editingId) {
        // Edit an existing pending record — amount / currency / comment etc.
        await api.put(`/other-expenses/${editingId}`, {
          amount: parseFloat(newExpense.amount),
          currency: newExpense.currency,
          expense_type: newExpense.expense_type,
          description: newExpense.description,
          notes: newExpense.description,
          date: newExpense.date,
        });
        toast.success("Expense updated");
      } else {
        await api.post("/other-expenses", {
          payee_name: user?.name || "Unknown",
          amount: parseFloat(newExpense.amount),
          currency: newExpense.currency,
          expense_type: newExpense.expense_type,
          description: newExpense.description,
          notes: newExpense.description, // Also save to notes for admin view
          date: newExpense.date,
          status: "pending",
          recurrence: newExpense.recurrence,
        });
        toast.success(
          newExpense.recurrence === "monthly"
            ? "Regular expense saved — a request will be created automatically each month"
            : "Expense submitted for approval"
        );
      }
      setDialogOpen(false);
      setEditingId(null);
      setNewExpense(emptyForm);
      fetchExpenses();
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error.response?.data?.detail || "Failed to submit expense");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return (
          <Badge variant="outline" className="bg-sky-500/10 text-sky-400 border-sky-500/20">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Approved
          </Badge>
        );
      case "paid":
        return (
          <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/20">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Paid
          </Badge>
        );
      case "cancelled":
        return (
          <Badge variant="outline" className="bg-zinc-700/50 text-zinc-500 border-zinc-600">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="bg-orange-500/10 text-orange-400 border-orange-500/20">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
    }
  };

  // Calculate totals
  const totals = expenses.reduce(
    (acc, exp) => {
      if (exp.status !== "cancelled") {
        acc.total += exp.amount || 0;
        if (exp.status === "paid") acc.paid += exp.amount || 0;
        else if (exp.status === "approved") acc.approved += exp.amount || 0;
        else acc.pending += exp.amount || 0;
      }
      return acc;
    },
    { total: 0, paid: 0, approved: 0, pending: 0 }
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="my-expenses-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">My Expenses</h1>
          <p className="text-sm text-zinc-500">Submit expenses for reimbursement</p>
        </div>
        
        <Button className="bg-orange-500 hover:bg-orange-600" data-testid="submit-expense-btn" onClick={openNew}>
          <Plus className="h-4 w-4 mr-2" />
          Submit Expense
        </Button>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditingId(null); setNewExpense(emptyForm); } }}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-zinc-50 flex items-center gap-2">
                <Send className="h-5 w-5 text-orange-400" />
                {editingId ? "Edit Expense" : "Submit New Expense"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Amount *</Label>
                  <Input
                    type="number"
                    value={newExpense.amount}
                    onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
                    placeholder="0"
                    className="bg-zinc-950 border-zinc-800 text-zinc-50"
                    data-testid="expense-amount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Currency</Label>
                  <Select
                    value={newExpense.currency}
                    onValueChange={(v) => setNewExpense({ ...newExpense, currency: v })}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="UAH" className="text-zinc-50">₴ UAH</SelectItem>
                      <SelectItem value="USD" className="text-zinc-50">$ USD</SelectItem>
                      <SelectItem value="EUR" className="text-zinc-50">€ EUR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Category *</Label>
                  <Select
                    value={newExpense.expense_type}
                    onValueChange={(v) => setNewExpense({ ...newExpense, expense_type: v })}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50" data-testid="expense-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {Object.entries(EXPENSE_CATEGORIES).map(([key, { label }]) => (
                        <SelectItem key={key} value={key} className="text-zinc-50">{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Date</Label>
                  <Input
                    type="date"
                    value={newExpense.date}
                    onChange={(e) => setNewExpense({ ...newExpense, date: e.target.value })}
                    className="bg-zinc-950 border-zinc-800 text-zinc-50"
                  />
                </div>
              </div>
              
              {!editingId && (
                <div className="space-y-2">
                  <Label className="text-zinc-300">Frequency</Label>
                  <Select
                    value={newExpense.recurrence}
                    onValueChange={(v) => setNewExpense({ ...newExpense, recurrence: v })}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50" data-testid="expense-recurrence-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="one_time" className="text-zinc-50">One-time</SelectItem>
                      <SelectItem value="monthly" className="text-zinc-50">Regular (monthly)</SelectItem>
                    </SelectContent>
                  </Select>
                  {newExpense.recurrence === "monthly" && (
                    <p className="text-xs text-zinc-500">
                      A pending request will be created automatically on the 15th of each month.
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-zinc-300">Description *</Label>
                <Textarea
                  value={newExpense.description}
                  onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
                  placeholder="What is this expense for?"
                  className="bg-zinc-950 border-zinc-800 text-zinc-50 min-h-[100px]"
                  data-testid="expense-description-input"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-zinc-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitExpense}
                  disabled={submitting}
                  className="bg-orange-500 hover:bg-orange-600"
                  data-testid="confirm-submit-btn"
                >
                  {submitting ? "Saving..." : (editingId ? "Save changes" : "Submit for Approval")}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Total</span>
              <DollarSign className="h-4 w-4 text-orange-500" />
            </div>
            <p className="text-2xl font-bold text-zinc-50">{formatAmount(totals.total, "UAH")}</p>
            <p className="text-xs text-zinc-500">{expenses.length} expenses</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Pending</span>
              <Clock className="h-4 w-4 text-orange-500" />
            </div>
            <p className="text-2xl font-bold text-orange-400">{formatAmount(totals.pending, "UAH")}</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Approved</span>
              <CheckCircle2 className="h-4 w-4 text-sky-500" />
            </div>
            <p className="text-2xl font-bold text-sky-400">{formatAmount(totals.approved, "UAH")}</p>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-zinc-500 uppercase font-mono">Paid</span>
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold text-green-400">{formatAmount(totals.paid, "UAH")}</p>
          </CardContent>
        </Card>
      </div>

      {/* Expenses List */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-50 text-lg">My Submitted Expenses</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {expenses.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <DollarSign className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>No expenses submitted yet</p>
              <p className="text-xs text-zinc-600 mt-1">Click "Submit Expense" to add your first expense</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400">Date</TableHead>
                  <TableHead className="text-zinc-400">Category</TableHead>
                  <TableHead className="text-zinc-400">Description</TableHead>
                  <TableHead className="text-zinc-400 text-right">Amount</TableHead>
                  <TableHead className="text-zinc-400 text-center">Status</TableHead>
                  <TableHead className="text-zinc-400 text-right w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((expense) => {
                  const category = EXPENSE_CATEGORIES[expense.expense_type] || EXPENSE_CATEGORIES.other;
                  const CategoryIcon = category.icon;
                  
                  return (
                    <TableRow
                      key={expense.id}
                      className="border-zinc-800 hover:bg-zinc-800/50"
                      data-testid={`expense-row-${expense.id}`}
                    >
                      <TableCell>
                        <span className="text-sm text-zinc-300 font-mono">
                          {expense.date ? new Date(expense.date).toLocaleDateString() : "—"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <CategoryIcon className={cn("h-4 w-4", category.color)} />
                          <span className={cn("text-sm", category.color)}>{category.label}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-zinc-50 text-sm flex items-center gap-2">
                          {expense.description || expense.notes || "—"}
                          {expense.recurrence === "monthly" && (
                            <Badge variant="outline" className="bg-indigo-500/10 text-indigo-300 border-indigo-500/30 text-[10px]">
                              <Repeat className="h-2.5 w-2.5 mr-0.5" /> Regular
                            </Badge>
                          )}
                          {expense.auto_created && (
                            <Badge variant="outline" className="bg-zinc-700/40 text-zinc-400 border-zinc-600 text-[10px]">auto</Badge>
                          )}
                        </p>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono text-zinc-50">{formatAmount(expense.amount, expense.currency)}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        {getStatusBadge(expense.status)}
                      </TableCell>
                      <TableCell className="text-right">
                        {expense.status === "pending" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEdit(expense)}
                            className="h-7 w-7 p-0 text-zinc-400 hover:text-zinc-50"
                            title="Edit"
                            data-testid={`edit-expense-${expense.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyExpensesPage;
